import { BTechApplicationFields as BT } from "../config/btechApplicationFields";

export type ValidationRule = {
    field: string;
    label: string;
    required?: boolean;
    custom?: (value: any, data: any) => string | null;
};

export const applicantValidationRules: ValidationRule[] = [
    { field: "fullname", label: "Full Name", required: true },
    { field: "email", label: "Email", required: true },
    { field: "mobile", label: "Mobile", required: true },
    { field: "dob", label: "Date of Birth", required: true },
    { field: "gender", label: "Gender", required: true },
    { field: "nationality", label: "Nationality", required: true },
    {
        field: "photo",
        label: "Photo",
        required: false, // Handled in custom to be more robust with File objects and existing string paths
        custom: (value: any) => {
            if (!value) return "Photo is required";
            if (value instanceof File && !value.name) return "Photo is required";
            if (typeof value === "string" && value.trim() === "") return "Photo is required";
            return null;
        }
    },
    {
        field: "abc_id", label: "ABC ID", required: true, custom: (value: any) => {
            if (!value) return null;

            if (!/^\d{12}$/.test(value)) {
                return "ABC ID must be exactly 12 digits";
            }

            return null;
        },
    },

    {
        field: "nationality",
        label: "Nationality",
        custom: (value: any, data: any) => {
            if (data.nationality !== "Indian" && (!value || value.trim() === "")) {
                return "Please specify nationality";
            }
            return null;
        },
    },
];

export const currentAddressValidationRules: ValidationRule[] = [
    { field: "line1", label: "Address Line 1", required: true },
    { field: "line2", label: "Address Line 2", required: true },
    { field: "city", label: "City", required: true },
    { field: "state", label: "State", required: true },
    { field: "country", label: "Country", required: true },
    { field: "pincode", label: "Pincode", required: true },
];

export const permAddressValidationRules: ValidationRule[] = [
    { field: "p_line1", label: "Address Line 1", required: true },
    { field: "p_line2", label: "Address Line 2", required: true },
    { field: "p_city", label: "City", required: true },
    { field: "p_state", label: "State", required: true },
    { field: "p_country", label: "Country", required: true },
    { field: "p_pincode", label: "Pincode", required: true },
];

export const guardianValidationRules: ValidationRule[] = [
    { field: "guardian_name", label: "Parent/Guardian Name", required: true },
    { field: "guardian_relation", label: "Parent/Guardian Relation", required: true },
    { field: "guardian_mobile", label: "Parent/Guardian Mobile", required: true },
    { field: "guardian_email", label: "Parent/Guardian Email", required: true },
];

export const education10thValidationRules: ValidationRule[] = [
    { field: "board", label: "Education Board", required: true },
    { field: "schoolname", label: "School/College Name", required: true },
    { field: "passingyear", label: "Year of Passing", required: true },
    { field: "marksformat", label: "Grade/Marks Type", required: true },
    { field: "marksobtained", label: "Marks Obtained", required: true },
    { field: "maximummarks", label: "Maximum Marks", required: true },
    { field: "grade", label: "Grade", required: true },
];

export const education12thValidationRules: ValidationRule[] = [
    { field: "board", label: "Education Board", required: true },
    { field: "schoolname", label: "School/College Name", required: true },
    {
        field: "percentagemaths",
        label: "Percentage of marks obtained in Mathematics",
        required: true,
        custom: (value) => {
            if (!value || String(value).trim() === "") return "Mathematics percentage is required";
            const num = parseFloat(String(value).trim());
            if (isNaN(num)) return "Invalid Mathematics percentage";
            if (num < 0 || num > 100) return "Invalid Mathematics percentage";
            return null;
        }
    },
    { field: "passingyear", label: "Year of Passing", required: true },
    { field: "marksformat", label: "Grade/Marks Type", required: true },
    { field: "marksobtained", label: "Marks Obtained", required: true },
    { field: "maximummarks", label: "Maximum Marks", required: true },
    { field: "grade", label: "Grade", required: true },
];

export const graduationValidationRules: ValidationRule[] = [
    { field: "collegename", label: "University Name", required: true },
    { field: "degree", label: "Degree", required: true },
    { field: "major", label: "Branch", required: true },
    { field: "passingyear", label: "Year of Passing", required: true },
    { field: "marks_obtained", label: "Marks/CGPA Obtained", required: true },
    { field: "maximum_marks", label: "Maximum Marks/CGPA", required: true },
];

export const programmePreferenceValidationRules: ValidationRule[] = [
    { field: "pref1", label: "Programme 1", required: true },
];

export const gateValidationRules: ValidationRule[] = [
    { field: "subjects", label: "GATE Subject", required: true },
    { field: "rollno", label: "Registration Number", required: true },
    { field: "score", label: "GATE Score", required: true },
    { field: "rank", label: "Rank", required: true },
    { field: "year", label: "Year", required: true },
    { field: "scorecard", label: "Scorecard", required: true },
];

/** B.Tech: 10th details stored on applications (see btechApplicationFields.ts). */
export const btechEducation10ApplicationRules: ValidationRule[] = [
    { field: BT.edu10.board, label: "10th — Education Board", required: true },
    { field: BT.edu10.schoolname, label: "10th — School/College", required: true },
    { field: BT.edu10.passingyear, label: "10th — Year of Passing", required: true },
    { field: BT.edu10.marksformat, label: "10th — Grade/Marks Type", required: true },
    {
        field: BT.edu10.percentage,
        label: "10th — Grade/Marks Type",
        required: true,
        custom: (value, data) => {
            if (!value || String(value).trim() === "") {
                return "10th Percentage is required.";
            }

            const percentage = parseFloat(String(value).trim());
            if (isNaN(percentage) || percentage < 60) {
                return "Minimum 60% is required to apply for B.Tech/IM.Tech programme.";
            }

            return null;
        }
    },
    {
        field: BT.edu10.marksobtained, label: "10th — Marks Obtained",
        custom: (value, data) => {
            const marksFormat = String(data[BT.edu10.marksformat] || "").toLowerCase();
            if (marksFormat.includes("grade")) {
                if (!value || String(value).trim() === "") {
                    return "10th — Marks Obtained";
                }
            }

            return null;
        }
    },
    {
        field: BT.edu10.maximummarks, label: "10th — Maximum Marks",
        custom: (value, data) => {
            const marksFormat = String(data[BT.edu10.marksformat] || "").toLowerCase();
            if (marksFormat.includes("grade")) {
                if (!value || String(value).trim() === "") {
                    return "10th — Maximum Marks";
                }
            }

            return null;
        }
    }
];

export const btechEducation12ApplicationRules: ValidationRule[] = [
    { field: BT.edu12.board, label: "12th — Education Board", required: true },
    { field: BT.edu12.schoolname, label: "12th — School/College", required: true },
    {
        field: BT.edu12.percentagemaths,
        label: "12th — Percentage of marks obtained in Mathematics",
        required: true,
        custom: (value) => {
            if (!value || String(value).trim() === "") return "Mathematics percentage is required";
            const num = parseFloat(String(value).trim());
            if (isNaN(num)) return "Invalid Mathematics percentage";
            if (num < 0 || num > 100) return "Invalid Mathematics percentage";
            return null;
        }
    },
    { field: BT.edu12.passingyear, label: "12th — Year of Passing", required: true },
    { field: BT.edu12.marksformat, label: "12th — Grade/Marks Type", required: true },
    {
        field: BT.edu12.percentage,
        label: "12th — Grade/Marks Type",
        required: true,
        custom: (value, data) => {
            if (!value || String(value).trim() === "") {
                return "12th Percentage is required.";
            }

            const percentage = parseFloat(String(value).trim());
            if (isNaN(percentage) || percentage < 60) {
                return "Minimum 60% is required to apply for B.Tech/IM.Tech programme.";
            }

            return null;
        }
    },
    {
        field: BT.edu12.marksobtained, label: "12th — Marks Obtained",
        custom: (value, data) => {
            const marksFormat = String(data[BT.edu12.marksformat] || "").toLowerCase();
            if (marksFormat.includes("grade")) {
                if (!value || String(value).trim() === "") {
                    return "12th — Marks Obtained";
                }
            }

            return null;
        }
    },
    {
        field: BT.edu12.maximummarks, label: "12th — Maximum Marks",
        custom: (value, data) => {
            const marksFormat = String(data[BT.edu12.marksformat] || "").toLowerCase();
            if (marksFormat.includes("grade")) {
                if (!value || String(value).trim() === "") {
                    return "12th — Maximum Marks";
                }
            }

            return null;
        }
    }
];

/** If any Olympiad field is filled, require the whole group. */
export const btechOlympiadApplicationRules: ValidationRule[] = [
    { field: BT.olympiad.name, label: "Olympiad Name", required: false },
    { field: BT.olympiad.no, label: "Olympiad Number", required: false },
    { field: BT.olympiad.proof, label: "Olympiad Proof", required: false },
];

export const btechOlympiadRequiredApplicationRules: ValidationRule[] = [
    { field: BT.olympiad.name, label: "Olympiad Name", required: true },
    { field: BT.olympiad.no, label: "Hall Ticket Number", required: true },
    { field: BT.olympiad.year, label: "Year of Olympiad", required: true },
    {
        field: BT.olympiad.proof,
        label: "Proof Document",
        required: false,
        custom: (value: any) => {
            if (!value) return "Proof Document is required";
            if (value instanceof File && !value.name) return "Proof Document is required";
            if (typeof value === "string" && value.trim() === "") return "Proof Document is required";
            return null;
        }
    },
];

export const jeeMainApplicationRules: ValidationRule[] = [
    // { field: BT.jeemain.examtype, label: "JEE Main — Exam type", required: true },
    { field: BT.jeemain.rollno, label: "JEE Main Enrolment Number", required: true },
    { field: BT.jeemain.score, label: "JEE Main Score", required: true },
    { field: BT.jeemain.rank, label: "JEE Main Rank (CRL)", required: true },
    {
        field: BT.jeemain.scorecard,
        label: "JEE Main Scorecard",
        required: false,
        custom: (value: any) => {
            if (!value) return "Scorecard is required";
            if (value instanceof File && !value.name) return "Scorecard is required";
            if (typeof value === "string" && value.trim() === "") return "Scorecard is required";
            return null;
        },
    },
];

export const jeeAdvancedApplicationRules: ValidationRule[] = [
    // { field: BT.jeeadv.examtype, label: "JEE Advanced — Exam type", required: true },
    { field: BT.jeeadv.rollno, label: "JEE Advanced — Roll number", required: false },
    { field: BT.jeeadv.score, label: "JEE Advanced — Score", required: false },
    { field: BT.jeeadv.rank, label: "JEE Advanced — Rank", required: false },
    {
        field: BT.jeeadv.scorecard,
        label: "JEE Advanced — Scorecard",
        required: false,
    },
];

export const updateJeeAdvancedApplicationRules: ValidationRule[] = [
    // { field: BT.jeeadv.examtype, label: "JEE Advanced — Exam type", required: true },
    { field: BT.jeeadv.rollno, label: "JEE Advanced — Roll number", required: true },
    { field: BT.jeeadv.score, label: "JEE Advanced — Score", required: true },
    { field: BT.jeeadv.rank, label: "JEE Advanced — Rank", required: true },
    {
        field: BT.jeeadv.scorecard,
        label: "JEE Advanced — Scorecard",
        required: false,
        custom: (value: any) => {
            if (!value) return "Scorecard is required";
            if (value instanceof File && !value.name) return "Scorecard is required";
            if (typeof value === "string" && value.trim() === "") return "Scorecard is required";
            return null;
        },
    },
];

export const jeeAdvancedRequiredApplicationRules: ValidationRule[] = [
    // { field: BT.jeeadv.examtype, label: "JEE Advanced — Exam type", required: true },
    { field: BT.jeeadv.rollno, label: "JEE Advanced Enrolment Number", required: true },
    { field: BT.jeeadv.score, label: "JEE Advanced Score", required: true },
    { field: BT.jeeadv.rank, label: "JEE Advanced Rank (CRL)", required: true },
    {
        field: BT.jeeadv.scorecard,
        label: "JEE Advanced Scorecard",
        required: false,
        custom: (value: any) => {
            if (!value) return "Scorecard is required";
            if (value instanceof File && !value.name) return "Scorecard is required";
            if (typeof value === "string" && value.trim() === "") return "Scorecard is required";
            return null;
        },
    },
];

// Registry for easier access if preferred
export const validationSchemas = {
    applicant: applicantValidationRules,
    currentAddress: currentAddressValidationRules,
    guardian: guardianValidationRules,
    education10th: education10thValidationRules,
    education12th: education12thValidationRules,
    graduation: graduationValidationRules,
    programmePreference: programmePreferenceValidationRules,
    gate: gateValidationRules,
};
